/**
 * AutoMod in the conversation (2026-10-29):
 * - `AutoModAlert`: Discord's AUTO_MODERATION_ACTION message (type 24) in a
 *   rule's alert channel: what was blocked or flagged, by whom, where, the
 *   rule and the word, with quick actions (jump to the message, delete it,
 *   time out, kick or ban the member) offered as your permissions allow.
 * - `AutoModBlockedNote`: what the sender sees on their own blocked message
 *   ("Only you can see this"), with the rule's response.
 * On a phone the actions wrap under the card; every one is a 36 tap target.
 */
import {
  alertField,
  isAutoModAlertEmbed,
  Permissions,
  type Message,
  type Snowflake,
} from '@neosocial/protocol';
import { useT } from '../i18n';
import { copyText } from '../lib/clipboard';
import { openModDialog } from '../mod/ModDialogs';
import { memberActions } from '../mod/perms';
import { useData, useDataShallow } from '../store/data';
import { deleteMessage, dropPending, type PendingMessage } from '../store/messages';
import { can, channelPermissions, userName } from '../store/selectors';
import { goHub, useUi } from '../store/ui';
import { Avatar, Btn, Icon } from '../ui/kit';
import { confirmDialog, toast } from '../ui/overlay';
import { renderMarkdown } from './markdown';
import type { MsgCtx } from './Message';
import { openProfile } from './ProfileCard';
import { reportError } from './social';

export function AutoModAlert({ m, ctx }: { m: Message; ctx: MsgCtx }) {
  const { t, fmt } = useT();
  const e = m.embeds.find(isAutoModAlertEmbed);
  const guildId = ctx.channel.guild_id ?? '';
  const name = useData((d) => userName(d, m.author.id, guildId));
  const author = useData((d) => d.users[m.author.id] ?? m.author);
  const where = e ? alertField(e, 'channel_id') : null;
  const flagged = e ? alertField(e, 'flagged_message_id') : null;
  const blocked = e ? alertField(e, 'decision_outcome') === 'blocked' : false;
  const channelName = useData((d) => (where ? d.channels[where]?.name : undefined));
  // the flagged message's channel: can you open it, and delete messages there?
  const there = useDataShallow((d) => {
    const c = where ? d.channels[where] : undefined;
    const p = c ? channelPermissions(d, c) : null;
    return {
      view: !!p && can(p, Permissions.VIEW_CHANNEL),
      manage: !!p && can(p, Permissions.MANAGE_MESSAGES),
    };
  });
  const acts = useDataShallow((d) => {
    const a = guildId ? memberActions(d, guildId, m.author.id) : null;
    return { timeout: !!a?.timeout, kick: !!a?.kick, ban: !!a?.ban };
  });
  if (!e) return null;
  const rule = alertField(e, 'rule_name') ?? '';
  const keyword = alertField(e, 'keyword');
  const matched = alertField(e, 'keyword_matched_content');
  const minutes = Number(alertField(e, 'timeout_duration') ?? 0);
  const channelLabel = channelName ? `#${channelName}` : t('am.alert.deletedChannel');
  const jump = (id: Snowflake) => {
    if (!where) return;
    goHub({ kind: 'channel', channelId: where });
    useUi.getState().set({ jump: { channelId: where, messageId: id } });
  };
  const del = async (id: Snowflake) => {
    if (!where) return;
    const ok = await confirmDialog({
      title: t('ns.msg.deleteTitle'),
      body: t('ns.msg.deleteBody'),
      confirmLabel: t('ns.msg.delete'),
      danger: true,
      icon: 'trash',
    });
    if (!ok) return;
    try {
      await deleteMessage(where, id);
      toast(t('am.alert.deletedMsg'), { icon: 'trash' });
    } catch (err) {
      reportError(err);
    }
  };
  const mod = (kind: 'timeout' | 'kick' | 'ban') =>
    openModDialog({ kind, guildId, userId: m.author.id });

  return (
    <article
      className="msg ns-am-alert"
      data-mid={m.id}
      id={`msg-${m.id}`}
      tabIndex={-1}
      data-outcome={blocked ? 'blocked' : 'flagged'}
    >
      <div className="msg-gut">
        <span className="ns-am-badge" aria-hidden="true">
          <Icon name="shield-check" size="m" />
        </span>
      </div>
      <div className="msg-body">
        <header className="msg-h">
          <span className="msg-name">{t('am.alert.by')}</span>
          <span className="ns-am-what">
            {t(blocked ? 'am.alert.blocked' : 'am.alert.flagged', { channel: channelLabel })}
          </span>
          <time className="msg-time" dateTime={m.timestamp}>
            {fmt.stamp(m.timestamp)}
          </time>
        </header>
        <div className="ns-am-card">
          <button
            type="button"
            className="ns-am-who"
            onClick={(ev) => openProfile(m.author.id, ev.currentTarget)}
          >
            <Avatar user={author} size={24} />
            <bdi>{name}</bdi>
          </button>
          <div className="msg-text ns-am-text" dir="auto">
            {renderMarkdown(m.content, ctx.resolver, t('hub.spoiler'))}
          </div>
          <dl className="ns-am-facts">
            <div>
              <dt>{t('am.alert.rule')}</dt>
              <dd dir="auto">{rule}</dd>
            </div>
            {keyword ? (
              <div>
                <dt>{t('am.alert.keyword')}</dt>
                <dd dir="auto">{keyword}</dd>
              </div>
            ) : null}
            {matched && matched !== keyword ? (
              <div>
                <dt>{t('am.alert.matched')}</dt>
                <dd dir="auto">{matched}</dd>
              </div>
            ) : null}
            {minutes > 0 ? (
              <div>
                <dt>{t('am.alert.timeout')}</dt>
                <dd>{t('am.alert.minutes', { n: minutes })}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        <div className="ns-am-acts" role="group" aria-label={t('am.alert.actions')}>
          {flagged && there.view ? (
            <Btn size="sm" kind="secondary" icon="arrow-right" onClick={() => jump(flagged)}>
              {t('am.alert.jump')}
            </Btn>
          ) : null}
          {flagged && there.manage ? (
            <Btn size="sm" kind="secondary" icon="trash" onClick={() => void del(flagged)}>
              {t('am.alert.deleteMsg')}
            </Btn>
          ) : null}
          {acts.timeout ? (
            <Btn size="sm" kind="secondary" icon="clock" onClick={() => mod('timeout')}>
              {t('am.alert.timeoutMember')}
            </Btn>
          ) : null}
          {acts.kick ? (
            <Btn size="sm" kind="secondary" icon="logout" onClick={() => mod('kick')}>
              {t('am.alert.kick')}
            </Btn>
          ) : null}
          {acts.ban ? (
            <Btn size="sm" kind="danger" icon="cm-ban" onClick={() => mod('ban')}>
              {t('am.alert.ban')}
            </Btn>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/** Your own message AutoMod blocked: only you see it; copy the text or dismiss it. */
export function AutoModBlockedNote({ p }: { p: PendingMessage }) {
  const { t } = useT();
  return (
    <div className="ns-pending-fail ns-am-blocked" role="alert">
      <Icon name="shield-check" size="s" />
      <span className="ns-am-blocked-t">
        <b>{t('am.blocked.title')}</b>{' '}
        {p.automod?.message ? <bdi className="ns-am-response">{p.automod.message}</bdi> : null}{' '}
        <span className="ns-muted">{t('am.blocked.only')}</span>
      </span>
      <button
        type="button"
        onClick={() =>
          void copyText(p.content).then(
            (ok) => ok && toast(t('am.blocked.copied'), { icon: 'copy' }),
          )
        }
      >
        {t('am.blocked.copy')}
      </button>
      <button type="button" onClick={() => dropPending(p.channelId, p.nonce)}>
        {t('am.blocked.dismiss')}
      </button>
    </div>
  );
}
